import * as yup from 'yup';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import React, { useState } from 'react';
import { Input } from 'reactstrap';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import request from 'Utils/Helper/Request';
import { useAuth } from 'Utils/Hooks/useAuth';
import authHelper from 'Utils/Helper/AuthHelpers';
import { Button, Field, ErrorNote } from 'Shared/UI';
import AuthLayout from './AuthLayout';

const loginSchema = yup.object().shape({
  email: yup.string().required('Enter your email address.'),
  password: yup.string().required('Enter your password.'),
});

const LoginForm = () => {
  const { setCurrentAdmin } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: { email: '', password: '' }, resolver: yupResolver(loginSchema) });

  const onSubmit = async (values) => {
    setError(null);
    try {
      const response = await request(`${process.env.REACT_APP_MTN_BASE_API_URL}/auth/login`, { method: 'POST', body: values });
      if (!response.status) {
        setError(response.message);
        return;
      }
      authHelper.setToken(response.data.access_token);
      // Feature switches (e.g. analytics_enabled) ride on the stored user; see Shell/features.js.
      const user = response.data.features ? { ...response.data.user, features: response.data.features } : response.data.user;
      authHelper.setUserInfo(user);
      setCurrentAdmin(user);
      toast.success('Signed in', { position: 'top-right' });
    } catch (err) {
      setError(err?.response?.payload?.message || 'Cannot reach the server. Check your connection and try again.');
    }
  };

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Use the email address and password for your Eudor account."
      footer={
        <>
          New to Eudor? <Link to={`${process.env.PUBLIC_URL}/auth/register`}>Create a partner account</Link>
        </>
      }
    >
      <form className="au-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <Field label="Email address" htmlFor="email" error={errors.email?.message}>
              <Input id="email" type="email" autoComplete="email" {...field} invalid={!!errors.email} />
            </Field>
          )}
        />
        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <Field label="Password" htmlFor="password" error={errors.password?.message}>
              <div className="au-pass">
                <Input id="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" {...field} invalid={!!errors.password} />
                <button type="button" onClick={() => setShowPassword((s) => !s)} aria-pressed={showPassword}>
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </Field>
          )}
        />
        <ErrorNote error={error} />
        <Button type="submit" busy={isSubmitting}>
          Sign in
        </Button>
      </form>
    </AuthLayout>
  );
};

export default LoginForm;
